import React, { useContext, useEffect } from "react";
import axios from "axios";
import yup from "~/utils/yup";
import { toast } from "react-toastify";
import MaskedInput from "~/shared/MaskedInput";
import TextInput from "~/shared/TextInput";
import Button from "~/shared/Btn";
import { Header, Content, Footer, WraperGrid } from "./styles";
import GoogleMapsInput from "~/shared/GoogleMapsInput";
import { FullAddress } from "~/shared/GoogleMapsInput/types";
import { Row, Col } from "react-bootstrap";
import { findRegionsByLocation } from "~/utils/regions";
import { MultiSelectFormContext } from "~/contexts/MultiSelectForm";
import DateInput from "~/shared/DateInput";
import SelectInput from "~/shared/SelectInput";
import { timeOptions, durationOptions } from "./options";
import { isAfterLimitOfDays, isPast, formatBrazil } from "~/utils/date";
import Head from "next/head";
import UfSelect from "~/shared/UfSelect";
import CitySelect from "~/shared/CitySelect";

type AddresFormProps = {
    goToStep?: (step: number) => void;
    prevStep?: () => void;
    nextStep?: () => void;
    currentStep?: number;
    regionComponents?: [];
};

const AddressForm = ({ currentStep, goToStep, nextStep, regionComponents = [] }: AddresFormProps) => {
    const {
        values,
        errors,
        setValues,
        updateValuesInLocalStorage,
        handleChange,
        handleBlur,
        updateStepStorage,
        confirmationStep,
        handleStatusUpdate,
        isAddressAfter,
        isDateAfter,
        setIsDateAfter,
        setIsAddressAfter,
    } = useContext(MultiSelectFormContext);

    const prevStep = () => {
        goToStep(currentStep - 1);
    };

    updateStepStorage("addressAndDate", currentStep);

    const findCep = async (value: string) => {
        if (!value) return;

        try {
            const response = await axios.get(`https://api.pagar.me/1/zipcodes/${value}`);
            handleChange("street", response.data.street);
            handleChange("neighborhood", response.data.neighborhood);
            handleChange("city", response.data.city);
            handleChange("state", response.data.state);
            handleChange("zipCode", response.data.zipCode);
        } catch (error) {
            return toast.error("CEP não encontrado");
        }
    };

    const validateAddress = async () => {
        const { zipcode, street, number, city, state } = values;

        const fields = { zipcode, number, street, city, state };
        let schema;

        if (isAddressAfter) {
            schema = yup.object().shape({
                city: yup.string().required(),
                state: yup.string().required(),
            });
        } else {
            schema = yup.object().shape({
                zipcode: yup.string().required(),
                street: yup.string().required(),
                number: yup.string().required(),
            });
        }

        return await schema.isValid(fields);
    };

    const validateDate = () => {
        if (isDateAfter) {
            return true;
        }
        if (!values.date || !values.duration || !values.hour) {
            return false;
        }

        const asDate = new Date(`${values.date}T00:00:00`);
        return isAfterLimitOfDays(asDate, 2);
    };

    const getErrorMessage = () => {
        if (values.date && values.hour && values.duration) {
            const asDate = new Date(`${values.date}T00:00:00`);

            if (isPast(asDate)) {
                return "Não é possível selecionar uma data anterior ao dia atual.";
            }

            const phone = "5511988721831";
            const whatsappMessage = encodeURIComponent(
                `[Fotop Serviços Marketplace]\nGostaria de marcar um job para o dia ${formatBrazil(asDate)}. Em ${
                    values.fullAddress
                }`
            );

            return (
                <>
                    Não é possível marcar uma data tão próxima. Selecione outra data ou{" "}
                    <a href={`https://wa.me/${phone}?text=${whatsappMessage}`} target="_blank" rel="noopener nofollow">
                        entre em contato conosco
                    </a>
                </>
            );
        }

        return "Os campos Data, Horário e Duração precisão ser preenchidos.";
    };

    const validateAndNextStep = async () => {
        if (!(await validateAddress())) {
            if (values.street) return toast.error("Preencha todos os campos obrigatórios.");
            return toast.error("Preencha todos os campos obrigatórios.");
        }

        if (!validateDate()) {
            return toast.error(getErrorMessage());
        }

        if (confirmationStep) {
            updateValuesInLocalStorage();
            return goToStep(confirmationStep);
        }
        nextStep();
    };

    const handleAddressChange = (_: string, value: FullAddress) => {
        const region =
            value.neighborhood && value.zipCode
                ? findRegionsByLocation(regionComponents, value.uf, value.cityName, value.neighborhood, value.zipCode)
                : null;

        return handleChange("fullAddress", { ...value, region }, { updateFn: handleStatusUpdate });
    };

    const handleAddressBlur = (_: string, value: FullAddress) => {
        handleBlur("fullAddress", value.fullAddress);
        handleBlur("address", value.address);
        handleBlur("number", value.number);
        handleBlur("complement", value.complement);
        handleBlur("neighborhood", value.neighborhood);
        handleBlur("zipCode", value.zipCode);
        handleBlur("cityName", value.cityName);
        handleBlur("uf", value.uf);
    };

    useEffect(() => {
        const localValues = JSON.parse(localStorage.getItem("values"));
        if (confirmationStep) {
            setValues(localValues);
        }
    }, []);

    useEffect(() => {
        if (values?.fullAddress || values?.street) {
            document.getElementById("number").focus();
        }
    }, [values?.fullAddress, values?.street]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (isAddressAfter) {
            handleChange("street", "");
            handleChange("fullAddress", "");
            handleChange("address", "");
            handleChange("number", "");
            handleChange("complement", "");
            handleChange("neighborhood", "");
            handleChange("zipcode", "");
        } else {
            handleChange("state", "");
            handleChange("city", "");
        }
    }, [isAddressAfter]);

    return (
        <>
            <Head>
                <title>Endereço e data do job - Fotop Serviços</title>
            </Head>
            <Header>
                <h1>Onde e quando a sessão fotográfica vai acontecer?</h1>
            </Header>
            <WraperGrid>
                <Content>
                    <form>
                        <Row>
                            <strong>Endereço</strong>
                        </Row>
                        <Row>
                            <Col lg={10} md={9} sm={12}>
                                <GoogleMapsInput
                                    name="address"
                                    autoComplete="chrome-off"
                                    role="presentation"
                                    placeholder="* Endereço"
                                    value={values?.fullAddress ? values?.fullAddress : values?.street}
                                    handleChange={handleAddressChange}
                                    handleBlur={handleAddressBlur}
                                    label=""
                                    disabled={isAddressAfter}
                                />
                            </Col>
                            <Col lg={2} md={3} sm={12}>
                                <TextInput
                                    name="number"
                                    placeholder="* Numero"
                                    value={values?.number}
                                    error={errors?.number}
                                    onChange={handleChange}
                                    disabled={isAddressAfter}
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col lg={5} md={4} sm={4}>
                                <TextInput
                                    name="neighborhood"
                                    placeholder="* Bairro"
                                    value={values?.neighborhood}
                                    onChange={handleChange}
                                    error={errors?.neighborhood}
                                    disabled={isAddressAfter}
                                />
                            </Col>

                            <Col lg={2} md={4} sm={4}>
                                {isAddressAfter ? (
                                    <UfSelect
                                        name="state"
                                        placeholder="* Estado"
                                        value={values?.state}
                                        onChange={handleChange}
                                        error={errors?.state}
                                    />
                                ) : (
                                    <TextInput
                                        name="state"
                                        placeholder="* Estado"
                                        value={values?.state}
                                        onChange={handleChange}
                                        error={errors?.state}
                                    />
                                )}
                            </Col>
                            <Col lg={5} md={4} sm={4}>
                                {isAddressAfter ? (
                                    <CitySelect
                                        name="city"
                                        placeholder="* Cidade"
                                        value={values?.city}
                                        onChange={handleChange}
                                        error={errors?.city}
                                        stateId={values?.state}
                                    />
                                ) : (
                                    <TextInput
                                        name="city"
                                        placeholder="* Cidade"
                                        value={values?.city}
                                        onChange={handleChange}
                                        error={errors?.city}
                                    />
                                )}
                            </Col>
                        </Row>

                        <Row>
                            <Col md={3} sm={4}>
                                <MaskedInput
                                    mask={[/\d/, /\d/, /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/]}
                                    guide={false}
                                    name="zipcode"
                                    placeholder="* CEP"
                                    value={values?.zipcode}
                                    onChange={handleChange}
                                    onBlur={(name, value) => findCep(value)}
                                    error={errors?.neighborhood}
                                    disabled={isAddressAfter}
                                />
                            </Col>
                            <Col md={9} sm={8}>
                                <TextInput
                                    name="complement"
                                    placeholder="Complemento"
                                    value={values?.complement}
                                    onChange={handleChange}
                                    disabled={isAddressAfter}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col style={{ display: "flex" }}>
                                <input
                                    style={{ marginRight: "0.5rem", cursor: "pointer" }}
                                    type="checkbox"
                                    checked={isAddressAfter}
                                    onClick={() => setIsAddressAfter(!isAddressAfter)}
                                />
                                <p style={{ fontSize: "12px" }}>Ainda não tenho endereço definido</p>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <p style={{ fontSize: "12px" }}>
                                    * Os campos Cidade e Estado são obrigatórios para a solicitação.
                                </p>
                            </Col>
                        </Row>
                    </form>
                </Content>
                <Content>
                    <div className="row">
                        <strong>Data</strong>
                    </div>
                    <DateInput
                        name="date"
                        placeholder="Data"
                        onChange={handleChange}
                        value={values.date}
                        disabled={isDateAfter}
                    />
                    <SelectInput
                        name="hour"
                        placeholder="Horário"
                        onChange={handleChange}
                        options={timeOptions}
                        value={values.hour}
                        disabled={isDateAfter}
                    />
                    <SelectInput
                        name="duration"
                        placeholder="Duração"
                        onChange={handleChange}
                        options={durationOptions}
                        value={values.duration}
                        disabled={isDateAfter}
                    />
                    <div style={{ display: "flex" }}>
                        <input
                            style={{ marginRight: "0.5rem", cursor: "pointer" }}
                            type="checkbox"
                            checked={isDateAfter}
                            onClick={() => setIsDateAfter(!isDateAfter)}
                        />
                        <p style={{ fontSize: "12px" }}>Ainda não tenho data definida</p>
                    </div>
                </Content>
            </WraperGrid>
            <Footer size={70}>
                {confirmationStep ? (
                    <Button block onClick={validateAndNextStep}>
                        Salvar
                    </Button>
                ) : (
                    <>
                        <Button id="clickAddressPrev" onClick={prevStep} block>
                            ◄ Passo anterior
                        </Button>
                        <Button id="clickAddressNext" block onClick={validateAndNextStep}>
                            Próximo passo ►
                        </Button>
                    </>
                )}
            </Footer>
        </>
    );
};

export default AddressForm;
